import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import type { CategoryWithSchema } from "./types";

interface DynamicAttributeFieldsProps {
  category: CategoryWithSchema | undefined;
  values: Record<string, any>;
  onChange: (updater: (prev: Record<string, any>) => Record<string, any>) => void;
}

export function DynamicAttributeFields({ category, values, onChange }: DynamicAttributeFieldsProps) {
  if (!category || !category.schema?.properties) return null;

  return (
    <div className="col-span-1 md:col-span-2 border-t border-muted/30 pt-6 mt-4">
      <h3 className="text-xs font-semibold mb-4 text-primary uppercase tracking-wider">
        {category.name} Specifications
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(category.schema.properties).map(([key, prop]: [string, any]) => {
          const label = prop.title || key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          const isRequired = category.schema.required?.includes(key);

          return (
            <div key={key} className="space-y-2">
              <Label htmlFor={`dyn-${key}`}>{label} {isRequired && '*'}</Label>
              {prop.enum ? (
                <Select
                  value={values[key] || ''}
                  onValueChange={(val) => onChange(prev => ({ ...prev, [key]: val }))}
                >
                  <SelectTrigger id={`dyn-${key}`}>
                    <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    {prop.enum.map((opt: string) => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : prop.type === 'boolean' ? (
                <Select
                  value={values[key] === true ? 'true' : values[key] === false ? 'false' : ''}
                  onValueChange={(val) => onChange(prev => ({ ...prev, [key]: val === 'true' }))}
                >
                  <SelectTrigger id={`dyn-${key}`}>
                    <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Yes</SelectItem>
                    <SelectItem value="false">No</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id={`dyn-${key}`}
                  type={prop.type === 'number' || prop.type === 'integer' ? 'number' : 'text'}
                  step={prop.type === 'number' ? 'any' : '1'}
                  value={values[key] ?? ''}
                  onChange={(e) => {
                    const val = e.target.value;
                    onChange(prev => ({
                      ...prev,
                      [key]: prop.type === 'number' || prop.type === 'integer'
                        ? val === '' ? null : Number(val)
                        : val
                    }));
                  }}
                  placeholder={`Enter ${label.toLowerCase()}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
